import { inject, Injectable } from '@angular/core';
import { catchError, Observable, of, throwError } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ApiResponse } from '../../../types/commons';
import { HttpStatus } from '../../utils/http-status.util';
import { normalizeApiErrors } from '../../utils/api-errors.util';
import {
  CreateFilingSessionResponse,
  ListSessionFilesResponse,
  UploadSessionFileResponse,
  SubmitFilingSessionResponse,
} from '../../../types/api-response';
import { RuntimeConfigService } from '../common/runtime-config.service';

@Injectable({
  providedIn: 'root',
})
export class FormSubmitService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(RuntimeConfigService);

  private readonly API_BASE = `${this.config.apiUrl()}/${this.config.apiVersion()}/mbrs`;
  private readonly API_URL_FILING_SESSIONS = `${this.API_BASE}/filing-sessions`;
  private readonly API_URL_PHASE_2 = `${this.API_BASE}/post-mbrs-data`; // retrieve full information for mbrs except FilingInfo
  private readonly API_URL_SUBMIT_FOR_VALIDATION = `${this.API_BASE}/submit-for-validation`; // Submit mbrs data for validation
  private readonly API_URL_ARCHIVE_CONVERSION = `${this.API_BASE}/archive-conversion`;

  // --- Filing Session 3-endpoint flow (replaces old post-filing-info) ---

  createFilingSession(fsFileName: string): Observable<ApiResponse<CreateFilingSessionResponse>> {
    return this.http
      .post<ApiResponse<CreateFilingSessionResponse>>(
        this.API_URL_FILING_SESSIONS,
        { fsFileName },
        { withCredentials: true }
      )
      .pipe(catchError(this.createErrorHandler()));
  }

  uploadSessionFile(sessionId: string, file: File): Observable<ApiResponse<UploadSessionFileResponse>> {
    const formData = new FormData();
    formData.append('File', file); // singular "File", not "Files"
    // No custom OS hint header. The backend used to parse `User-Agent` (and
    // `Sec-CH-UA-Platform` on Chromium) to pick a byte-unit validator; it
    // stopped on 2026-07-10. Every size it reports is decimal now, on every
    // client, so there is no OS-dependent decision left to hint at.
    return this.http
      .post<ApiResponse<UploadSessionFileResponse>>(
        `${this.API_URL_FILING_SESSIONS}/${sessionId}/files`,
        formData,
        { withCredentials: true }
      )
      .pipe(catchError(this.createErrorHandler(file.name)));
  }

  listSessionFiles(sessionId: string): Observable<ApiResponse<ListSessionFilesResponse[]>> {
    return this.http
      .get<ApiResponse<ListSessionFilesResponse[]>>(
        `${this.API_URL_FILING_SESSIONS}/${sessionId}/files`,
        { withCredentials: true }
      )
      .pipe(catchError(this.createErrorHandler()));
  }

  /**
   * Clears every file already uploaded into a session, leaving the session
   * itself open and refreshing its expiry window.
   *
   * This is how `FilingSessionUploader` reconciles a session after the user
   * changes their file selection. Creating a fresh session instead would
   * strand this one, orphan the files inside it, and leave an abandoned
   * Conversion row behind.
   */
  deleteSessionFiles(sessionId: string): Observable<ApiResponse<null>> {
    return this.http
      .delete<ApiResponse<null>>(
        `${this.API_URL_FILING_SESSIONS}/${sessionId}/files`,
        { withCredentials: true }
      )
      .pipe(catchError(this.createErrorHandler()));
  }

  /**
   * Starts Phase 1 (prerequisite extraction) for an uploaded session.
   *
   * `engine` is the optional per-request engine override chosen at the
   * dashboard picker. The property is omitted from the body entirely when it is
   * null, so the server falls back to its configured `Conversion:Engine` — an
   * absent body property, not an explicit null, because the backend's body
   * parameter is optional and the flag-off path must keep posting the bare
   * `{}` it has always posted.
   *
   * The value is a STRING on the wire (`"v1"`, `"v1.5"`, `"v2"`), never the
   * backend enum's ordinal. See `ConversionEngineOption.value`.
   */
  submitFilingSession(
    sessionId: string,
    engine?: string | null,
  ): Observable<ApiResponse<SubmitFilingSessionResponse>> {
    const body = engine ? { conversionEngine: engine } : {};
    return this.http
      .post<ApiResponse<SubmitFilingSessionResponse>>(
        `${this.API_URL_FILING_SESSIONS}/${sessionId}/submit`,
        body,
        { withCredentials: true }
      )
      .pipe(catchError(this.createErrorHandler()));
  }

  submitXBRLPhaseTwoForm(formData: any, fileName?: string): Observable<ApiResponse> {
    return this.http
      .post<ApiResponse>(this.API_URL_PHASE_2, formData, {
        withCredentials: true,
      })
      .pipe(catchError(this.createErrorHandler(fileName)));
  }

  submitForValidation(formData: any): Observable<ApiResponse> {
    return this.http
      .post<ApiResponse>(this.API_URL_SUBMIT_FOR_VALIDATION, formData, {
        withCredentials: true,
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          // Any non-2xx response for submit-for-validation is a HARD fault: the
          // backend did NOT persist. It must NOT be swallowed into a
          // `{ status:false }` response, because
          // `FormValidationSubmitService.submitForValidationWithData` would then
          // misread it as `validation-failed` ("backend persisted → proceed") and
          // `SubmitForValidationComponent` would commit local state and navigate
          // to `/mbrs/xbrl-files`. Re-throw EVERY error so it reaches the
          // component's `tag:'error'` branch — a red error toast while the user
          // stays on the form, with no commit/refresh/navigation.
          //
          // 401 is not special-cased here: re-throwing already covers it, and
          // `sessionExpiryInterceptor` independently adds the forced-logout
          // redirect for a 401 (it ignores every other status). So 400, 404, 410,
          // 413, 5xx and network-0 all just show the toast and stay put; only a
          // 401 additionally logs the user out.
          return throwError(() => error);
        })
      );
  }

  archiveConversion(formData: object, fileName?: string): Observable<ApiResponse> {
    return this.http
      .post<ApiResponse>(
        this.API_URL_ARCHIVE_CONVERSION,
        { ...formData },
        {
          withCredentials: true,
        }
      )
      .pipe(catchError(this.createErrorHandler(fileName)));
  }

  /**
   * Creates an error handler with the file name in closure.
   *
   * ORDERING IS LOAD-BEARING: the status branches run BEFORE the generic
   * `error.error.errors` passthrough. Until 2026-07-10 the passthrough came
   * first and tested `errors.length > 0` — truthy for a non-empty STRING, which
   * is exactly what the backend sends on a 413. That shadowed the 413 branch
   * below and made it dead code. Do not move the passthrough back up.
   *
   * Every read of `.errors` goes through `normalizeApiErrors`, because the wire
   * sends `string` on 413 and `string[]` elsewhere despite the `ApiResponse`
   * type claiming `string[]` throughout.
   *
   * NOTE: this handler does not forward the HttpErrorResponse — it synthesizes
   * a fresh `ApiResponse` from it. Any field not copied below is therefore
   * silently dropped before a caller can ever see it. `errorCode` is spread
   * into EVERY branch for that reason; a branch that forgets it turns a stable,
   * machine-readable failure back into environment-dependent prose.
   *
   * @param fileName Optional file name to include in error message
   */
  private createErrorHandler(fileName?: string) {
    return (error: HttpErrorResponse): Observable<ApiResponse> => {
      const body = error.error as {
        errors?: unknown;
        errorCode?: unknown;
      } | null;
      const serverErrors = normalizeApiErrors(body?.errors);

      // Omitted rather than set to undefined when absent, mirroring the wire
      // format — the backend leaves the field out entirely when there is no code.
      const code: Pick<ApiResponse, 'errorCode'> | Record<string, never> =
        typeof body?.errorCode === 'string' && body.errorCode.length > 0
          ? { errorCode: body.errorCode }
          : {};

      if (error.status === HttpStatus.Gone) {
        return of({
          ...code,
          status: false,
          statusCode: HttpStatus.Gone,
          data: null,
          errors: ['Filing session has expired. Please start a new submission.'],
        });
      }

      if (error.status === HttpStatus.NotFound) {
        return of({
          ...code,
          status: false,
          statusCode: HttpStatus.NotFound,
          data: null,
          errors: ['Filing session not found. Please start a new submission.'],
        });
      }

      // Two distinct 413s, both surfaced with `statusCode: 413` so callers can
      // branch on one code:
      //   a) the file cleared the transport limit but exceeded the per-file cap —
      //      the server saw it and names the numbers in `errors`;
      //   b) the request body exceeded the ~52,000,000-byte transport limit — the
      //      request was killed during model binding, so the server never saw the
      //      file and cannot name it.
      // We preserve whichever text arrived. `FilingSessionUploader` composes its
      // own message from the real cap and the local `file.size`, and only falls
      // back to this text when it has not fetched the limits.
      if (error.status === HttpStatus.PayloadTooLarge) {
        return of({
          ...code,
          status: false,
          statusCode: HttpStatus.PayloadTooLarge,
          data: null,
          errors:
            serverErrors.length > 0
              ? serverErrors
              : [
                  'Upload is too large for the server to accept. Please reduce the number or size of files and try again.',
                ],
        });
      }

      if (serverErrors.length > 0) {
        return of({
          ...code,
          status: false,
          statusCode: error.status || HttpStatus.InternalServerError,
          data: null,
          errors: serverErrors,
        });
      }

      const fileNameText = fileName || '[File name not available]';
      return of({
        ...code,
        status: false,
        statusCode: error.status || HttpStatus.InternalServerError,
        data: null,
        errors: [`Failed to convert ${fileNameText}. Please try again later.`],
      });
    };
  }
}
